import { create } from 'zustand';
import { getObjects } from '../api/generated/objects/objects';
import { useToastStore } from './useToastStore';
import i18n from '@/i18n/config';
import type {
  FacilityFlowCoreModulesCustomersDtosCreateObjectRequest,
  FacilityFlowCoreModulesCustomersDtosReorderObjectsRequest,
  FacilityFlowCoreModulesCustomersDtosListObjectsResponse,
} from '../api/generated/models';
import type { UpdateObjectRequestWithGeo, ObjectDtoWithGeo } from '../types/objects-api';

type ObjectDto = ObjectDtoWithGeo;

type CreateObjectRequestWithGeo = FacilityFlowCoreModulesCustomersDtosCreateObjectRequest & {
  latitude?: number | null;
  longitude?: number | null;
};

type CreateObjectRequest = CreateObjectRequestWithGeo;
type UpdateObjectRequest = UpdateObjectRequestWithGeo;
type ReorderObjectsRequest = FacilityFlowCoreModulesCustomersDtosReorderObjectsRequest;
type ListObjectsResponse = FacilityFlowCoreModulesCustomersDtosListObjectsResponse;

export type ObjectSortBy = 'Name' | 'CreatedAt' | 'DisplayOrder';
export type SortDir = 'Asc' | 'Desc';

export type ObjectQuery = {
  search?: string;
  searchTerm?: string;
  customerId?: string;
  isActive?: boolean;
  sortBy?: ObjectSortBy;
  sortDir?: SortDir;
  page?: number;
  pageSize?: number;
  includeTasks?: boolean;
  includeInactiveTasks?: boolean;
};

const {
  getApiV1Objects,
  getApiV1ObjectsId,
  postApiV1Objects,
  putApiV1ObjectsId,
  deleteApiV1ObjectsId,
  putApiV1ObjectsCustomersCustomerIdReorder,
  patchApiV1ObjectsIdActiveOffer,
} = getObjects();

interface ObjectState {
  objects: ObjectDto[];
  /** True when `objects` holds every object, i.e. the last `fetchAllObjects` ran without a filter. */
  allObjectsLoaded: boolean;
  selectedObject: ObjectDto | null;
  loading: boolean;
  saving: boolean;
  error: string | null;
  totalCount: number;
  page: number;
  pageSize: number;

  query: ObjectQuery;
  setQuery: (partial: Partial<ObjectQuery>) => void;

  fetchObjects: (params?: ObjectQuery) => Promise<void>;
  fetchAllObjects: (filter?: { customerId?: string; search?: string; type?: string; isActive?: boolean }) => Promise<void>;
  getObjectById: (id: string) => Promise<ObjectDto | null>;
  createObject: (object: CreateObjectRequest) => Promise<void>;
  updateObject: (id: string, object: UpdateObjectRequest) => Promise<void>;
  deleteObject: (id: string) => Promise<void>;
  reorderObjects: (customerId: string, request: ReorderObjectsRequest) => Promise<void>;
  setActiveOffer: (objectId: string, offerId: string | null) => Promise<boolean>;
}

export const useObjectStore = create<ObjectState>((set, get) => ({
  objects: [],
  allObjectsLoaded: false,
  selectedObject: null,
  loading: false,
  saving: false,
  error: null,
  totalCount: 0,
  page: 1,
  pageSize: 20,

  query: { page: 1, pageSize: 20, sortBy: 'Name', sortDir: 'Asc', includeTasks: false },
  setQuery: (partial) => set({ query: { ...get().query, ...partial } }),

  fetchObjects: async (params?: ObjectQuery) => {
    const query = { ...get().query, ...(params || {}) };

    set({ loading: true, error: null, query });
    try {
      // Orval currently exposes only a subset of params; cast is intentional until next generate:api
      const response = await getApiV1Objects(query as UnknownRecord);
      const pageData = response as unknown as ListObjectsResponse;

      set({
        objects: (pageData.items || []) as unknown as ObjectDto[],
        allObjectsLoaded: false,
        totalCount: pageData.totalCount || 0,
        page: pageData.page || query.page || 1,
        pageSize: pageData.pageSize || query.pageSize || 20,
        loading: false,
      });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Fehler beim Laden der Objekte',
        loading: false,
      });
    }
  },

  fetchAllObjects: async (filter?: { customerId?: string; search?: string; type?: string; isActive?: boolean }) => {
    set({ loading: true, error: null });
    try {
      const pageSize = 200;
      const all: ObjectDto[] = [];
      let total = 0;
      for (let page = 1; page <= 50; page += 1) {
        const resp = await getApiV1Objects({
          ...filter,
          page,
          pageSize,
          sortBy: 'Name',
          sortDir: 'Asc',
        } as UnknownRecord);
        const data = resp as unknown as ListObjectsResponse;
        const items = (data.items ?? []) as unknown as ObjectDto[];
        all.push(...items);
        total = data.totalCount ?? all.length;
        if (items.length === 0 || all.length >= total) break;
      }
      set({
        objects: all,
        allObjectsLoaded: !filter || Object.values(filter).every((value) => value === undefined),
        totalCount: total,
        loading: false,
      });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Fehler beim Laden der Objekte',
        loading: false,
      });
    }
  },

  getObjectById: async (id: string) => {
    set({ loading: true, error: null });
    try {
      const object = (await getApiV1ObjectsId(id)) as unknown as ObjectDto;
      set({ selectedObject: object, loading: false });
      return object;
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Fehler beim Laden des Objekts',
        loading: false,
        selectedObject: null,
      });
      return null;
    }
  },

  createObject: async (objectData: CreateObjectRequest) => {
    set({ saving: true, error: null });
    try {
      // why: the generated types now carry the full contract (incl. lat/lon), so the payload
      // is passed through as-is — no cast needed.
      await postApiV1Objects(objectData);
      set({ saving: false });

      useToastStore.getState().addToast('Objekt erfolgreich erstellt', 'success');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Fehler beim Erstellen des Objekts';
      set({
        error: errorMessage,
        saving: false,
      });

      useToastStore.getState().addToast(errorMessage, 'error');
      throw error;
    }
  },

  updateObject: async (id: string, objectData: UpdateObjectRequest) => {
    set({ saving: true, error: null });
    try {
      await putApiV1ObjectsId(id, objectData);
      set({ saving: false });

      useToastStore.getState().addToast('Objekt erfolgreich aktualisiert', 'success');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Fehler beim Aktualisieren des Objekts';
      set({
        error: errorMessage,
        saving: false,
      });

      useToastStore.getState().addToast(errorMessage, 'error');
      throw error;
    }
  },

  deleteObject: async (id: string) => {
    set({ saving: true, error: null });
    try {
      await deleteApiV1ObjectsId(id);
      set({ saving: false });

      useToastStore.getState().addToast('Objekt erfolgreich gelöscht', 'success');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Fehler beim Löschen des Objekts';
      set({
        error: errorMessage,
        saving: false,
      });

      useToastStore.getState().addToast(errorMessage, 'error');
      throw error;
    }
  },

  reorderObjects: async (customerId: string, request: ReorderObjectsRequest) => {
    set({ saving: true, error: null });
    try {
      await putApiV1ObjectsCustomersCustomerIdReorder(customerId, request);
      set({ saving: false });

      useToastStore.getState().addToast('Objekte erfolgreich neu sortiert', 'success');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Fehler beim Sortieren der Objekte';
      set({
        error: errorMessage,
        saving: false,
      });

      useToastStore.getState().addToast(errorMessage, 'error');
      throw error;
    }
  },

  setActiveOffer: async (objectId: string, offerId: string | null) => {
    set({ saving: true, error: null });
    try {
      await patchApiV1ObjectsIdActiveOffer(objectId, { offerId });
      const reloaded = (await getApiV1ObjectsId(objectId)) as unknown as ObjectDto;
      set((state) => ({
        saving: false,
        selectedObject: reloaded,
        objects: state.objects.map((o) => (o.id === objectId ? reloaded : o)),
      }));
      useToastStore.getState().addToast(i18n.t('offers:messages.activeOfferSet'), 'success');
      return true;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : i18n.t('offers:messages.activeOfferError');
      set({ error: errorMessage, saving: false });
      useToastStore.getState().addToast(errorMessage, 'error');
      return false;
    }
  },
}));

type UnknownRecord = Record<string, unknown>;
